import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { CircularProgress, TextField, Tooltip } from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import styles from '../style/common.module.css';

type AddTextFieldProps = {
  onSubmit: (value: string) => Promise<any>;
  textEditLabel: string;
  editMinWidth?: string;
  maxLength?: number;
  rows?: number;
  initialValue?: string;
  addIcon: ReactNode;
  allowEmpty?: boolean;
  hoverText?: string;
};

export const AddTextField = (props: AddTextFieldProps) => {
  const [value, setValue] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const { onSubmit, textEditLabel, rows, maxLength, editMinWidth, addIcon, allowEmpty, hoverText } = props;
  const inputRef = useRef<HTMLInputElement>(null);

  const isMultiline = rows != undefined && rows > 1;

  useEffect(() => {
    if (inputRef?.current && open) {
      inputRef.current.focus();
      inputRef.current.setSelectionRange(value.length, value.length);
    }
  }, [open]);

  useEffect(() => {
    setValue(props.initialValue || '');
  }, [props.initialValue]);

  const cancel = async () => {
    setOpen(false);
    setValue(props.initialValue || '');
  };

  const submit = async () => {
    setOpen(false);
    setLoading(true);
    try {
      if (allowEmpty || value.length != 0) {
        await onSubmit(value);
      }
    } finally {
      setLoading(false);
      if (!props.initialValue) {
        setValue('');
      }
    }
  };

  return (
    <div>
      {loading && <CircularProgress sx={{ mr: '2px', color: '#007ca3', verticalAlign: 'middle' }} size={18} color="inherit" />}
      {!loading && !open && (
        <Tooltip title={hoverText && <span style={{ whiteSpace: 'pre-wrap' }}>{hoverText}</span>}>
          <div
            className={styles.centered}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setOpen(true);
            }}
          >
            {addIcon}
          </div>
        </Tooltip>
      )}

      {open && (
        <div>
          <TextField
            sx={{ mr: 1, mt: 1, minWidth: editMinWidth }}
            size="small"
            inputRef={inputRef}
            value={value}
            maxRows={rows || 1}
            multiline={isMultiline}
            inputProps={{
              maxLength: maxLength || 1024,
            }}
            onBlur={e => {
              cancel();
            }}
            onChange={e => setValue(e.target.value)}
            onKeyDown={async e => {
              if (e.key == 'Escape') {
                cancel();
              }
              if (e.key == 'Enter' && !isMultiline) {
                submit();
              }
            }}
            required={true}
            label={textEditLabel}
            variant="outlined"
          />
          {isMultiline && (
            <div style={{ display: 'flex', alignContent: 'space-between', marginTop: '2px' }}>
              <div style={{ flexGrow: 5 }}></div>
              <div>
                <DoneIcon
                  cursor="pointer"
                  color="success"
                  onMouseDown={e => {
                    // This is to make sure onBlur doesn't take precedence over clicking this button
                    e.preventDefault();
                  }}
                  onClick={() => submit()}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
